// typescript
import React from "react";
import Avatar from "@/main/react/buttons/ProfileImage";
import SeriesButtons from "@/main/react/series/SeriesButtons";
import {Localizer} from "@/main/react/Types";
import DateFormatter from "@/main/common/DateFormatter";
import {getDisplayUsername, getUserProfilePath, hasDisplayUsername} from "@/main/common/UsernameDisplayUtils";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import { getFriendlyPrivateBadgeLabel } from "@/main/react/card/CardMetadataBadgeCopy";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { useThumbnailLightbox } from "@/main/react/media/useThumbnailLightbox";
import type { SeriesReadingState } from "@/main/react/series/SeriesReadingTypes";
import { ChevronDown } from "lucide-react";
import { languageDisplayName } from "@/main/util/languageDisplayName";

/**
 * The resting pill, shared by the tappable discussions chip and the caption chips beside it.
 *
 * All three chips now reference this constant. Until `SERIES-10` two of them carried a byte-identical
 * inline copy of the string, which is why the register row could describe them as sharing it and be
 * describing an outcome rather than a mechanism. The resting recipe is deliberately common to the
 * interactive and inert families (`2026-08-11-header-count-chips-as-navigation-design.md` §3.1); the
 * differentiator lives in `SERIES_NAV_CHIP_*` below, never in here.
 */
const SERIES_COUNT_CHIP_CLASSES = "rounded-full border border-border bg-muted px-3 py-1 text-xs text-muted-foreground";

/**
 * `SERIES-10` — the resting affordance that survives a device with no hover.
 *
 * The discussions chip is a real button that smooth-scrolls to the list it counts; the followers and
 * language chips are captions. Its only resting differences were `standard-hover` and `cursor-pointer`,
 * both pointer-only, so on the surface's priority viewport the three chips were identical at rest and
 * the affordance was not merely hard to find, it was ABSENT: a member either never discovers it or taps
 * an inert neighbour and learns the page is broken.
 *
 * This is `EMPTY-STATES-59`'s mechanism, ported. `CardMetadataBadges.tsx` closed exactly this for the
 * card and profile chip family in wave 8; the series header is a hand-rolled button that never went
 * through that component, so the fix never reached it. Three properties are copied deliberately and
 * must not be "simplified" later:
 *
 *   1. The glyph is `absolute`. IN FLOW it widens the chip on hover, and in a `flex-wrap` row that is a
 *      non-converging feedback loop rather than a transition — grow, wrap to the next line, lose hover,
 *      shrink, return under the pointer, grow. Observed oscillating indefinitely on devo at the
 *      2026-08-18 gate, to the point the chip could not be clicked at all. Out of flow, the resting
 *      width equals the hovered width by construction and there is no arithmetic to get wrong.
 *   2. The reveal is gated on the INPUT DEVICE, not a viewport breakpoint. A touchscreen laptop is wide
 *      and coarse; a narrow desktop window is small and fine. Design-language rule 2.
 *   3. Only `opacity` and `transform` animate. Neither reflows.
 *
 * One deliberate divergence from the card family: `ChevronDown`, not `ChevronRight`. Those chips
 * navigate to another surface; this one scrolls to a list directly below the header, so a down caret is
 * the truthful glyph. The TREATMENT is byte-identical, which is what §3.1 binds when it says the two
 * families must not drift on hover or focus.
 */
const SERIES_NAV_CHIP_INTERACTIVE_CLASSES =
    "group/nav-chip standard-hover relative touch-hit-44 cursor-pointer [@media(pointer:coarse)]:pr-4 focus-visible:outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50";

/**
 * Drawn inside the padding the chip already has (`right-1`, sized `2.5` = 10px, so a right extent of
 * 14px against the 16px `pr-4` reserve above). Reserved under the SAME gate that reveals it, so the
 * band exists exactly when a glyph is drawn in it.
 */
const SERIES_NAV_CHIP_CHEVRON_CLASSES =
    "pointer-events-none absolute right-1 top-1/2 inline-flex -translate-y-1/2 translate-x-0.5 items-center opacity-0 transition-[opacity,transform] duration-150 ease-out group-hover/nav-chip:translate-x-0 group-hover/nav-chip:opacity-100 group-focus-visible/nav-chip:translate-x-0 group-focus-visible/nav-chip:opacity-100 [@media(pointer:coarse)]:translate-x-0 [@media(pointer:coarse)]:opacity-100";

export type SeriesHeaderDisplayerProps = {
    seriesPublicId?: string;
    seriesSlug?: string;
    title: string;
    ownerUsername: string | undefined;
    ownerProfileImageUrl?: string;
    thumbnailUrl: string | undefined;
    displayThumbnailUrl?: string;
    createdAt: string;
    description: string | undefined;
    mainLocale?: string;
    discussionsCount?: number | null;
    followersCount?: number | null;
    initiallyOwner: boolean;
    initiallyFollowing?: boolean;
    privateAccess: boolean;
    bookmarksCollection?: boolean;
    sensitive?: boolean;
    seriesReadingState?: SeriesReadingState;
    /**
     * Makes the discussions count chip scroll to the list it counts.
     *
     * A scroll rather than a route change, and only on this one chip: the series page has exactly one
     * list, so there is no second destination to navigate to, and a chip pushing a history entry to stay
     * on the same screen would cost a Back press for nothing. The followers chip stays a caption because
     * the product has no series-followers list to open. Decided 2026-08-11, §2.3 of
     * `designs/01-now/2026-08-11-header-count-chips-as-navigation-design.md`.
     */
    onDiscussionsCountClick?: () => void;
    localizer: Localizer;

};

export default function SeriesHeaderDisplayer({
                                                  seriesPublicId,
                                                  seriesSlug,
                                                  title = "",
                                                  ownerUsername,
                                                  ownerProfileImageUrl,
                                                  thumbnailUrl,
                                                  displayThumbnailUrl,
                                                  createdAt,
                                                  description,
                                                   mainLocale,
                                                   discussionsCount,
                                                   followersCount,
                                                  initiallyOwner = false,
                                                  initiallyFollowing = false,
                                                  privateAccess = false,
                                                   bookmarksCollection = false,
                                                   sensitive = false,
                                                                                                  seriesReadingState,
                                                   onDiscussionsCountClick,
                                                  localizer,
                                              }: SeriesHeaderDisplayerProps) {
    const dateFormatter = new DateFormatter(localizer);
    const hasOwnerUsername = hasDisplayUsername(ownerUsername);
    const displayOwnerUsername = getDisplayUsername(ownerUsername, localizer);
    const ownerProfilePath = getUserProfilePath(ownerUsername);
    const descriptionText = description?.trim() || localizer("series.no_description");
    const createdText = createdAt ? dateFormatter.timeSince(createdAt) : null;
    const privateVisibilityLabel = getFriendlyPrivateBadgeLabel(localizer, "badge.series.private", privateAccess);
    const displayedDiscussionsCount = Number(discussionsCount ?? 0);
    const displayedFollowersCount = Number(followersCount ?? 0);
    const seriesLanguageName = languageDisplayName(localizer, mainLocale);
    // `SV-134` — see the tile below.
    const { openZoom: openThumbnailZoom, lightbox: thumbnailLightbox } = useThumbnailLightbox({ localizer, site: "series" });
    return (
        <section className="mb-6 w-full" data-series-public-id={seriesPublicId} data-playwright-id="series-header">
            <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
                <div className="flex flex-col gap-5">
                    <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
                        <div className="shrink-0 self-start rounded-xl bg-muted/80 p-2 shadow-[inset_0_1px_0_color-mix(in_srgb,var(--text-inverse)_10%,transparent)]" data-playwright-id="series-header-thumbnail">
                            {/* `SV-134`. A TERMINAL site: the member is already on the series this tile
                                identifies, so there is nowhere to navigate to and the tile opens the
                                picture. `role="terminal"` also withholds the affordance entirely when
                                the series has no cover — a full-screen letter tile is a worse state
                                than an inert one. */}
                            <Avatar localizer={localizer}
                                imageUrl={displayThumbnailUrl ?? thumbnailUrl}
                                username={title }
                                className={"size-24 sm:size-32 lg:size-36"}
                                mode="series"
                                role="terminal"
                                onClick={() => openThumbnailZoom(displayThumbnailUrl ?? thumbnailUrl, title)}
                            />
                        </div>

                        <div className="min-w-0 flex-1 pt-1">
                            {privateVisibilityLabel ? (
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-neutral-text">
                                    <span className="h-1 w-1 rounded-full bg-neutral/50" />
                                    <span>{privateVisibilityLabel}</span>
                                </div>
                            ) : null}

                            <h1 className={`${privateVisibilityLabel ? "mt-2" : "mt-0"} max-w-full break-words text-2xl font-semibold tracking-tight text-text-main sm:text-3xl`}>
                                <span className="inline-flex flex-wrap items-center gap-2">
                                    <span>{title}</span>
                                    {sensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                                </span>
                            </h1>

                            <AuthorAttribution localizer={localizer}
                                name={displayOwnerUsername}
                                href={hasOwnerUsername ? ownerProfilePath : null}
                                imageUrl={ownerProfileImageUrl}
                                avatarSeed={ownerUsername ?? displayOwnerUsername}
                                details={createdText ? <time dateTime={createdAt}>{createdText}</time> : null}
                                className="mt-3"
                                avatarClassName="size-7"
                            />

                            <p className="mt-4 max-w-3xl text-sm leading-6 text-muted-foreground sm:text-[15px]">
                                {descriptionText}
                            </p>

                              <div className="mt-4 flex flex-wrap gap-2">
                                  {/* Same hover/focus treatment as the group header's count chips, so
                                      the two chip families cannot drift on interaction. */}
                                  {onDiscussionsCountClick ? (
                                      <button
                                          type="button"
                                          onClick={onDiscussionsCountClick}
                                          data-playwright-id="series-header-count-discussions"
                                          className={`${SERIES_COUNT_CHIP_CLASSES} ${SERIES_NAV_CHIP_INTERACTIVE_CLASSES}`}
                                      >
                                          {localizer("badge.series.discussions", String(displayedDiscussionsCount))}
                                          {/* `aria-hidden` because it is an affordance, not information:
                                              the chip's accessible name stays the count phrase. */}
                                          <span className={SERIES_NAV_CHIP_CHEVRON_CLASSES} aria-hidden={true}>
                                              <ChevronDown className="size-2.5" />
                                          </span>
                                      </button>
                                  ) : (
                                      <div className={SERIES_COUNT_CHIP_CLASSES} data-playwright-id="series-header-count-discussions">
                                          {localizer("badge.series.discussions", String(displayedDiscussionsCount))}
                                      </div>
                                  )}
                                  {/* Real ICU plurals, not `{n} {noun}` — "1 posts" (`DISCUSSION-20`),
                                      and a concatenated count/noun pair cannot be made correct at all
                                      in a locale with more than two plural forms. The chips still
                                      render at zero, so these localize directly rather than through
                                      `getFriendlyCountBadgeLabel`, which returns null there. */}
                                  <div className={SERIES_COUNT_CHIP_CLASSES} data-playwright-id="series-header-count-followers">
                                      {localizer("badge.series.followers", String(displayedFollowersCount))}
                                  </div>
                                  {/* SERIES-7 — the LANGUAGE, not the subtag. This chip used to print
                                      `mainLocale` raw, so it read `en` beside two complete sentences,
                                      and it read `en` in all 25 UI locales: the value bypassed the
                                      localizer entirely, so neither key parity nor the verbatim guard
                                      could see the hole. Gated on the resolved NAME rather than on the
                                      raw tag so a tag that resolves to nothing renders no chip at all
                                      instead of an empty pill. Tracked as SV-73 opportunity #1, whose
                                      cited line has drifted. */}
                                  {seriesLanguageName ? (
                                      <div className={SERIES_COUNT_CHIP_CLASSES} data-playwright-id="series-header-language">
                                          {seriesLanguageName}
                                      </div>
                                  ) : null}
                              </div>
                        </div>
                    </div>

                    <div className="relative z-layer-raised-front flex w-full max-w-full items-center gap-2 overflow-visible">
                        <SeriesButtons
                            seriesPublicId={seriesPublicId ?? ""}
                            seriesSlug={seriesSlug ?? ""}
                            initiallyOwner={initiallyOwner}
                            initiallyFollowing={initiallyFollowing}
                            seriesTitle={title}
                            seriesDescription={description ?? ""}
                            seriesThumbnailUrl={thumbnailUrl ?? ""}
                            mainLocale={mainLocale}
                            privateAccess={privateAccess}
                            bookmarksCollection={bookmarksCollection}
                            sensitive={Boolean(sensitive)}
                            seriesReadingState={seriesReadingState}
                            discussionsCount={displayedDiscussionsCount}
                            surface="series_header"
                            localizer={localizer}
                            allowWrap={true}
                            showMetricsEntry={true}
                        />
                    </div>
                </div>
            </div>

            {/* `SV-134` — the viewer for the terminal tile above. `fixed`-positioned, so where it
                sits in the tree is irrelevant to where it paints. */}
            {thumbnailLightbox}
        </section>
    );
}
